export interface ShaperOpts {
  letterSpacing?: number;
  kerning?: boolean;
  features?: Record<string, boolean>;
  vertical?: boolean;
  /** Photoshop Optical kerning at this typed size (WasmShaper: Rust `optical`); the font's `kern` is off then. */
  opticalKern?: number;
}

export interface Shaper {
  unitsPerEm(fontRef: string): number;
  /** os2.sCapHeight in font units; 0 if the table is absent. */
  capHeightUnits(fontRef: string): number;
  /** yMax of the glyph for `ch` in font units; 0 if the char maps to .notdef (index 0). */
  glyphYMax(fontRef: string, ch: string): number;
  /** yMin of the glyph for `ch` in font units (negative below the baseline); 0 if .notdef. */
  glyphYMin(fontRef: string, ch: string): number;
  /** opentype glyph index for `ch`; 0 if .notdef. */
  charGlyphId(fontRef: string, ch: string): number;
  usWeightClass(fontRef: string): number;
  /** Equivalent to opentype.js getAdvanceWidth. */
  measureAdvance(fontRef: string, text: string, sizePx: number, opts: ShaperOpts): number;
  /** Equivalent to opentype.js forEachGlyph — positions already include kerning + letterSpacing. */
  layoutRun(fontRef: string, text: string, sizePx: number, baselineY: number, opts: ShaperOpts): RunGlyph[];
}

/**
 * Глиф прогона. `cluster` — индекс (UTF-16) символа `text`, из которого глиф вышел; `undefined` — шейпер
 * соответствие не знает (лигатуры opentype.js).
 */
export interface RunGlyph {
  glyphId: number;
  x: number;
  y: number;
  cluster?: number;
}

/** UTF-16-индексы начала каждой кодовой точки строки, по порядку. */
function codePointStarts(text: string): number[] {
  const starts: number[] = [];
  let i = 0;
  for (const ch of text) {
    starts.push(i);
    i += ch.length;
  }

  return starts;
}

/** UTF-8-смещение (cluster rustybuzz) → UTF-16-индекс той же кодовой точки. */
function utf16IndexOfUtf8Offsets(text: string): Map<number, number> {
  const map = new Map<number, number>();
  let utf8 = 0;
  let utf16 = 0;
  for (const ch of text) {
    map.set(utf8, utf16);
    const cp = ch.codePointAt(0) ?? 0;
    utf8 += cp < 0x80 ? 1 : cp < 0x800 ? 2 : cp < 0x10000 ? 3 : 4;
    utf16 += ch.length;
  }

  return map;
}

interface GlyphLike {
  index?: number;
  advanceWidth?: number;
  leftSideBearing?: number;
  /** The hmtx advance, kept by opentype.js once a `getTransform` has written an advance at some axes over `advanceWidth`. */
  _advanceWidth?: number;
}

/** opentype.js `font.variation.process`: the HVAR delta of a glyph metric at the axes, written onto no glyph. */
interface OpentypeVariationLike {
  process: {
    getVariableAdjustment(glyphIndex: number, table: 'hvar', metric: 'advanceWidth', coords: FontVariationLike): number;
  };
}

interface FontMetricsLike {
  yMax: number;
  yMin: number;
}

/** An opentype.js `fvar` axis: its tag padded to four characters and its range. */
interface FvarAxisLike {
  tag?: string;
  minValue?: number;
  maxValue?: number;
}

// Clamped to the file's range before normalizing, as ttf-parser does: opentype.js gives a value past it no HVAR delta.
function clampToAxes(variation: FontVariationLike, axes: FvarAxisLike[] | undefined): FontVariationLike {
  const out: FontVariationLike = {};
  for (const [tag, value] of Object.entries(variation)) {
    const axis = axes?.find(a => a.tag === tag.padEnd(4, ' '));
    const aboveMin = axis?.minValue === undefined ? value : Math.max(value, axis.minValue);
    out[tag] = axis?.maxValue === undefined ? aboveMin : Math.min(aboveMin, axis.maxValue);
  }

  return out;
}

export interface OpentypeFontLike {
  unitsPerEm: number;
  tables: { os2?: { sCapHeight?: number }; hvar?: unknown; fvar?: { axes?: FvarAxisLike[] } };
  charToGlyph(c: string): { index?: number; getMetrics(): FontMetricsLike };
  forEachGlyph(
    text: string,
    x: number | undefined,
    y: number | undefined,
    fontSize: number | undefined,
    options: ShaperOpts,
    callback: (glyph: GlyphLike, gx: number, gy: number) => void,
  ): number;
  getAdvanceWidth(text: string, fontSize?: number, options?: ShaperOpts): number;
  /** Present on a variable font only. */
  variation?: OpentypeVariationLike;
}

// GPOS pair kerning stays at the default instance: opentype.js drops the Device/VariationIndex deltas of a ValueRecord.
export class OpentypeShaper implements Shaper {
  private readonly font: OpentypeFontLike;
  private readonly weightClass: number;
  private readonly variation?: FontVariationLike;
  private readonly advanceByGlyph = new Map<number, number>();

  /** `variation` — the axes the engine draws this face at (as `WasmShaper` takes them); ignored on a static font. */
  constructor(font: OpentypeFontLike, weightClass: number, variation?: FontVariationLike) {
    this.font = font;
    this.weightClass = weightClass;
    // A copy: opentype.js pads short axis tags in the coords object it is handed.
    this.variation = variation && font.variation ? clampToAxes(variation, font.tables.fvar?.axes) : undefined;
  }

  unitsPerEm(_fontRef: string): number {
    return this.font.unitsPerEm;
  }

  capHeightUnits(_fontRef: string): number {
    return this.font.tables.os2?.sCapHeight ?? 0;
  }

  glyphYMax(_fontRef: string, ch: string): number {
    return this.font.charToGlyph(ch).getMetrics().yMax;
  }

  glyphYMin(_fontRef: string, ch: string): number {
    return this.font.charToGlyph(ch).getMetrics().yMin;
  }

  charGlyphId(_fontRef: string, ch: string): number {
    // undefined index means the font found a real glyph; only explicit 0 is .notdef
    return this.font.charToGlyph(ch).index ?? -1;
  }

  usWeightClass(_fontRef: string): number {
    return this.weightClass;
  }

  measureAdvance(_fontRef: string, text: string, sizePx: number, opts: ShaperOpts): number {
    if (!this.variation) return this.font.getAdvanceWidth(text, sizePx, opts);
    let shift = 0;
    const end = this.font.forEachGlyph(text, 0, 0, sizePx, opts, g => {
      shift += this.advanceShift(g, sizePx);
    });
    return end + shift;
  }

  layoutRun(_fontRef: string, text: string, sizePx: number, baselineY: number, opts: ShaperOpts): RunGlyph[] {
    const result: RunGlyph[] = [];
    let shift = 0;
    this.font.forEachGlyph(text, 0, baselineY, sizePx, opts, (g, gx, gy) => {
      result.push({ glyphId: g.index ?? 0, x: gx + shift, y: gy });
      if (this.variation) shift += this.advanceShift(g, sizePx);
    });
    // Без лигатур opentype.js кладёт по глифу на кодовую точку — тогда соответствие известно точно.
    const starts = codePointStarts(text);
    if (starts.length === result.length) result.forEach((g, i) => (g.cluster = starts[i]));
    return result;
  }

  // The pen of opentype.js moves by the glyph's live advance, kerning and spacing on top; this tops it up to the axes.
  private advanceShift(glyph: GlyphLike, sizePx: number): number {
    return ((this.advanceAtAxes(glyph) - (glyph.advanceWidth || 0)) * sizePx) / this.font.unitsPerEm;
  }

  // Delta read, not applied: getTransform writes shared glyphs. No HVAR keeps hmtx, though the engine reads gvar phantoms.
  private advanceAtAxes(glyph: GlyphLike): number {
    const cached = glyph.index === undefined ? undefined : this.advanceByGlyph.get(glyph.index);
    if (cached !== undefined) return cached;
    const base = glyph._advanceWidth ?? glyph.advanceWidth ?? 0;
    const variation = this.font.variation;
    if (glyph.index === undefined || !variation || !this.font.tables.hvar) return base;
    const delta = variation.process.getVariableAdjustment(glyph.index, 'hvar', 'advanceWidth', this.variation ?? {});
    const advance = Math.round(base + delta);
    this.advanceByGlyph.set(glyph.index, advance);
    return advance;
  }
}

/** Deterministic shaper for unit tests — no font file required. */
export class FakeShaper implements Shaper {
  private readonly upm: number;
  private readonly capH: number;
  private readonly wght: number;

  constructor(upm = 1000, capH = 700, wght = 400) {
    this.upm = upm;
    this.capH = capH;
    this.wght = wght;
  }

  unitsPerEm(_fontRef: string): number {
    return this.upm;
  }

  capHeightUnits(_fontRef: string): number {
    return this.capH;
  }

  glyphYMax(_fontRef: string, ch: string): number {
    // .notdef for NUL; otherwise a fixed fraction of upm
    return ch.charCodeAt(0) === 0 ? 0 : Math.round(this.upm * 0.7);
  }

  glyphYMin(_fontRef: string, _ch: string): number {
    return 0;
  }

  charGlyphId(_fontRef: string, ch: string): number {
    return ch.charCodeAt(0);
  }

  usWeightClass(_fontRef: string): number {
    return this.wght;
  }

  measureAdvance(_fontRef: string, text: string, sizePx: number, _opts: ShaperOpts): number {
    return text.length * sizePx * 0.5;
  }

  layoutRun(_fontRef: string, text: string, sizePx: number, baselineY: number, _opts: ShaperOpts): RunGlyph[] {
    const result: RunGlyph[] = [];
    let cx = 0;
    for (let i = 0; i < text.length; i++) {
      result.push({ glyphId: text.charCodeAt(i), x: cx, y: baselineY, cluster: i });
      cx += sizePx * 0.5;
    }

    return result;
  }
}

/** Routes each call by its `fontRef` to that face's shaper (runs drawn with their own fonts); unknown refs go to `fallback`. */
export class FontRefShaper implements Shaper {
  constructor(
    private readonly byRef: ReadonlyMap<string, Shaper>,
    private readonly fallback: Shaper,
  ) {}

  private of(fontRef: string): Shaper {
    return this.byRef.get(fontRef) ?? this.fallback;
  }

  unitsPerEm(fontRef: string): number {
    return this.of(fontRef).unitsPerEm(fontRef);
  }

  capHeightUnits(fontRef: string): number {
    return this.of(fontRef).capHeightUnits(fontRef);
  }

  glyphYMax(fontRef: string, ch: string): number {
    return this.of(fontRef).glyphYMax(fontRef, ch);
  }

  glyphYMin(fontRef: string, ch: string): number {
    return this.of(fontRef).glyphYMin(fontRef, ch);
  }

  charGlyphId(fontRef: string, ch: string): number {
    return this.of(fontRef).charGlyphId(fontRef, ch);
  }

  usWeightClass(fontRef: string): number {
    return this.of(fontRef).usWeightClass(fontRef);
  }

  measureAdvance(fontRef: string, text: string, sizePx: number, opts: ShaperOpts): number {
    return this.of(fontRef).measureAdvance(fontRef, text, sizePx, opts);
  }

  layoutRun(fontRef: string, text: string, sizePx: number, baselineY: number, opts: ShaperOpts): RunGlyph[] {
    return this.of(fontRef).layoutRun(fontRef, text, sizePx, baselineY, opts);
  }
}

/** Axis values by OpenType tag, as the engine's `FontVariation` takes them (`{ wght: 450, slnt: -10 }`). */
export type FontVariationLike = Record<string, number>;

/** The WASM `TextShaper` surface (engine/psd-render/wasm) — an interface so tests can mock it. */
export interface TextShaperLike {
  register_font(fontRef: string, bytes: Uint8Array): void;
  has_font(fontRef: string): boolean;
  shape(fontRef: string, text: string, optsJson: string): string;
  font_metrics(fontRef: string): string;
  /** Rust caret grid of one text box (JSON) by the layout build_scene draws it with; absent on an older pkg. */
  caret_geometry?(boxJson: string, mappedFaceJson: string): string;
}

interface WasmShapedGlyph {
  glyph_id: number;
  cluster: number;
  x_advance: number;
  y_advance: number;
  x_offset: number;
  y_offset: number;
  y_max: number;
  y_min: number;
}

interface WasmFontMetrics {
  units_per_em: number;
  cap_height: number;
  us_weight_class: number;
}

// Подбор кегля меряет те же строки на каждом шаге, а шейпер живёт одну рамку: глифы в единицах шрифта от кегля
// не зависят, поэтому кэш общий на движок — ключ шрифт + признаки + текст (ref шрифта = его адрес, начертание).
const RUN_CACHE_LIMIT = 8192;
const RUN_CACHES = new WeakMap<TextShaperLike, Map<string, WasmShapedGlyph[]>>();

// rustybuzz advances come back in font units; scaled to px here so the WASM-side cache stays size-agnostic.
export class WasmShaper implements Shaper {
  private readonly engine: TextShaperLike;
  private readonly fontRef: string;
  private readonly variation?: FontVariationLike;
  private metricsCache?: WasmFontMetrics | null;
  private readonly charCache = new Map<string, WasmShapedGlyph | undefined>();
  private readonly runCache: Map<string, WasmShapedGlyph[]>;

  constructor(engine: TextShaperLike, fontRef: string, variation?: FontVariationLike) {
    this.engine = engine;
    this.fontRef = fontRef;
    this.variation = variation;
    this.runCache = RUN_CACHES.get(engine) ?? new Map();
    RUN_CACHES.set(engine, this.runCache);
  }

  private metrics(): WasmFontMetrics | null {
    if (this.metricsCache === undefined) {
      const raw = this.engine.font_metrics(this.fontRef);
      this.metricsCache = raw && raw !== 'null' ? (JSON.parse(raw) as WasmFontMetrics) : null;
    }

    return this.metricsCache;
  }

  private shapeRun(text: string, opts: ShaperOpts): WasmShapedGlyph[] {
    const features: Record<string, boolean> = { ...(opts.features ?? {}) };
    if (opts.kerning !== undefined) features['kern'] = !!opts.kerning;
    const optsJson = JSON.stringify({
      features,
      variation: this.variation ?? null,
      vertical: opts.vertical ?? false,
      optical_kern: opts.opticalKern ?? null,
    });
    const key = `${this.fontRef}\u0000${optsJson}\u0000${text}`;
    const cached = this.runCache.get(key);
    if (cached) return cached;

    const raw = this.engine.shape(this.fontRef, text, optsJson);
    const glyphs = raw ? (JSON.parse(raw) as WasmShapedGlyph[]) : [];
    if (this.runCache.size >= RUN_CACHE_LIMIT) this.runCache.delete(this.runCache.keys().next().value!);
    this.runCache.set(key, glyphs);
    return glyphs;
  }

  private shapeChar(ch: string): WasmShapedGlyph | undefined {
    if (!this.charCache.has(ch)) {
      this.charCache.set(ch, this.shapeRun(ch, {})[0]);
    }

    return this.charCache.get(ch);
  }

  unitsPerEm(_fontRef: string): number {
    return this.metrics()?.units_per_em ?? 1000;
  }

  capHeightUnits(_fontRef: string): number {
    return this.metrics()?.cap_height ?? 0;
  }

  glyphYMax(_fontRef: string, ch: string): number {
    return this.shapeChar(ch)?.y_max ?? 0;
  }

  glyphYMin(_fontRef: string, ch: string): number {
    return this.shapeChar(ch)?.y_min ?? 0;
  }

  charGlyphId(_fontRef: string, ch: string): number {
    return this.shapeChar(ch)?.glyph_id ?? 0;
  }

  usWeightClass(_fontRef: string): number {
    return this.metrics()?.us_weight_class ?? 400;
  }

  measureAdvance(fontRef: string, text: string, sizePx: number, opts: ShaperOpts): number {
    if (!text) return 0;
    const scale = sizePx / (this.unitsPerEm(fontRef) || 1000);
    const ls = (opts.letterSpacing ?? 0) * sizePx;
    let total = 0;
    for (const g of this.shapeRun(text, opts)) {
      const adv = opts.vertical ? Math.abs(g.y_advance) : g.x_advance;
      total += adv * scale + ls;
    }

    return total;
  }

  layoutRun(fontRef: string, text: string, sizePx: number, baselineY: number, opts: ShaperOpts): RunGlyph[] {
    if (!text) return [];
    const scale = sizePx / (this.unitsPerEm(fontRef) || 1000);
    const ls = (opts.letterSpacing ?? 0) * sizePx;
    const out: RunGlyph[] = [];
    const clusterOf = utf16IndexOfUtf8Offsets(text);
    let cursor = 0;
    for (const g of this.shapeRun(text, opts)) {
      const cluster = clusterOf.get(g.cluster);
      if (opts.vertical) {
        // baselineY = column top; cursor walks down by |y_advance|, x = in-column offset
        out.push({ glyphId: g.glyph_id, x: g.x_offset * scale, y: baselineY + cursor - g.y_offset * scale, cluster });
        cursor += Math.abs(g.y_advance) * scale + ls;
      } else {
        out.push({ glyphId: g.glyph_id, x: cursor + g.x_offset * scale, y: baselineY - g.y_offset * scale, cluster });
        cursor += g.x_advance * scale + ls;
      }
    }

    return out;
  }
}
